import React, { useState } from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import { FaExternalLinkAlt, FaGithub, FaCode } from "react-icons/fa";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

const Projects = () => {
  const projects = [
    {
      name: "Contrôle Documentaire IA",
      badge: "Portfolio Cheffe de Projet IA",
      image: "/assets/img/project-ocr.jpg",
      stack: ["React", "Node.js / Express", "n8n", "Mistral OCR", "Jest"],
      description:
        "Système de lecture de pièces d'identité assisté par IA : l'OCR extrait les données d'une pièce (permis, carte d'identité, titre de séjour, passeport), un décodage MRZ (bande de lecture automatique imprimée en bas des pièces, avec ses propres chiffres de contrôle) les recoupe pour fiabiliser chaque champ. Lecture et décision de conformité au dossier sont volontairement séparées — l'IA ne juge jamais seule.",
      demo: "https://ocr-permis-frontend.onrender.com",
    },
    {
      name: "Murmure",
      badge: "Projet de fin de formation",
      image: "/assets/img/MurmureApp2.png",
      stack: [
        "React Native / Expo",
        "Node.js / Express",
        "MongoDB",
        "Socket.IO",
        "IA (Hugging Face)",
      ],
      description:
        "Application mobile de bien-être (MVP) proposant un parcours pédagogique, des méditations guidées (audio) et des exercices de respiration animés avec retours haptiques. Intégration d’un chat conversationnel en temps réel avec génération de réponses via IA.",
      demo: "https://player.cloudinary.com/embed/?cloud_name=dmbowwvt8&public_id=D%C3%A9mo_Murmure_MVP2&profile=cld-default",
      repoFront: "https://github.com/ElysaG/Murmure-Final-Front.git",
      repoBack: "https://github.com/ElysaG/Murmure-Final-Back.git",
    },
    {
      name: "My Moviz",
      image: "/assets/img/project-mymoviz.webp",
      stack: ["React", "JavaScript", "Node.js", "Express", "API REST"],
      description:
        "Application web full-stack permettant d'afficher les derniers films, de consulter leurs informations et de gérer une liste de favoris.Recherche locale et tri dynamique (notes, titres). Communication avec un backend Node/Express déployé en serverless.",
      demo: "https://my-moviz-front-orcin.vercel.app",
      repoFront: "https://github.com/ElysaG/MyMoviz-Front.git",
      repoBack: "https://github.com/ElysaG/MyMoviz-Back.git",
    },
    {
      name: "WeatherApp",
      image: "/assets/img/project-weather.webp",
      stack: ["React", "Node.js", "Express", "MongoDB", "OpenWeatherMap API"],
      description:
        "Application météo full-stack affichant les conditions en temps réel via l'API OpenWeatherMap. Gestion des villes, suppression, stockage persistant en MongoDB.",
      demo: "https://weather-app4-front-ten.vercel.app/",
      repoFront: "https://github.com/ElysaG/WeatherApp4-Front.git",
      repoBack: "https://github.com/ElysaG/WeatherApp4-Back.git",
    },
    
    {
      name: "ChatApp",
      image: "/assets/img/project-chatapp.webp",
      stack: [
        "React Native / Expo",
        "Node.js / Express",
        "WebSockets",
        "Pusher",
      ],
      description:
        "Application mobile de messagerie instantanée réalisée avec React Native et Pusher. Chaque utilisateur choisit un pseudo et peut échanger en temps réel grâce à un backend Node/Express déployé sur Render.",
      demo: "https://expo.dev/preview/update?message=Changement+du+logo&updateRuntimeVersion=1.0.0&createdAt=2025-11-28T17%3A53%3A51.509Z&slug=exp&projectId=51fe1e21-3e76-49c2-8717-ad7c63b936b3&group=0a8a2b9a-9b27-4bd9-9b32-615481c59e2c",
      repoFront: "https://github.com/ElysaG/ChatApp-Front.git",
      repoBack: "https://github.com/ElysaG/ChatApp-Back.git",
    },
    {
      name: "Countries App",
      image: "/assets/img/project-countries.webp",
      stack: ["React", "JavaScript", "SASS", "API REST", "React Router"],
      description:
        "Application React permettant d'explorer les pays via une API publique, avec affichage des drapeaux, filtres dynamiques et navigation par routing.",
      repoFront: "https://github.com/ElysaG/CountriesApp.git",
    },
  ];

  return (
    <section id="projects" className="projects">
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: false, amount: 0.5 }}
      >
        Projets
      </motion.h2>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
        viewport={{ once: true, amount: 0.2 }}
      >
        <p className="swipe-hint">← Glissez pour voir →</p>

        <Swiper
          pagination={{
            type: "progressbar",
          }}
          navigation={true}
          modules={[Pagination, Navigation]}
          className="mySwiper"
          spaceBetween={30}
          slidesPerView={1}
          breakpoints={{
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
        >
          {projects.map((project, index) => (
            <SwiperSlide key={index}>
              <ProjectCard project={project} />
            </SwiperSlide>
          ))}
        </Swiper>
      </motion.div>
    </section>
  );
};

// Composant ProjectCard avec effet 3D Tilt
const ProjectCard = ({ project }) => {
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);

  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;

    const rotateYValue = (mouseX / (rect.width / 2)) * 10;
    const rotateXValue = -(mouseY / (rect.height / 2)) * 10;

    setRotateX(rotateXValue);
    setRotateY(rotateYValue);
  };

  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
  };

  return (
    <motion.div
      className="project-card"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
        transition: "transform 0.1s ease-out",
      }}
      whileHover={{ y: -5 }}
    >
      <div className="card-image">
{project.badge && (
    <span className="project-badge">{project.badge}</span>
  )}
        <img src={project.image} alt={project.name} />
        <div className="image-overlay"></div>
      </div>

      <div className="card-content">
        <h3>{project.name}</h3>

        <p className="description">{project.description}</p>

        {/* Tags Stack */}
        <div className="stack-tags">
          {project.stack.map((tech, index) => (
            <span key={index} className="tag">
              {tech}
            </span>
          ))}
        </div>

        {/* Boutons avec icônes */}
        <div className="project-links">
          {project.demo && (
            <a
              href={project.demo}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-link btn-demo"
            >
              <FaExternalLinkAlt />
              <span>Démo</span>
            </a>
          )}

          {project.repoFront && (
            <a
              href={project.repoFront}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-link btn-code"
            >
              <FaGithub />
              <span>{project.repoBack ? "Front" : "Code"}</span>
            </a>
          )}

          {project.repoBack && (
            <a
              href={project.repoBack}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-link btn-code"
            >
              <FaCode />
              <span>Back</span>
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Projects;
